/**
 * MarketplaceDashboard - Unified Dashboard for Lead Marketplace
 *
 * A tab-based entry point for new users providing easy navigation to:
 * - Lead Marketplace (My Listings, Browse, Received Interests, My Interests)
 * - Commission Payouts (Discover Payout, My Payouts, Payout Enquiries, Sent Enquiries)
 * - Lead Deals (My Deals, Lead Conversions)
 *
 * Features:
 * - Lazy loading at tab and sub-tab level using React.lazy()
 * - Compact card design (~30% smaller than standard)
 * - Mobile responsive with bottom navigation
 * - 12 records per section with "View All" navigation
 * - URL parameter tracking for browser back/forward navigation
 *   URL format: ?tab=marketplace&sub=listings (or browse, received, interests)
 *              ?tab=dsa&sub=browse (or offers, received, sent)
 *              ?tab=collaborations&sub=active (or completed)
 */

import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { SpeedDial } from 'primereact/speeddial';
import { Skeleton } from 'primereact/skeleton';
import { walletService, formatCurrency } from '../../services/walletService';

// URL parameter mappings for tabs
const MAIN_TAB_MAP = {
    marketplace: 0,
    dsa: 1,
    collaborations: 2
};

const MAIN_TAB_REVERSE = {
    0: 'marketplace',
    1: 'dsa',
    2: 'collaborations'
};

const SUB_TAB_MAP = {
    marketplace: { browse: 0, listings: 1, received: 2, interests: 3 },
    dsa: { browse: 0, offers: 1, received: 2, sent: 3 },
    collaborations: { active: 0, completed: 1 }
};

const SUB_TAB_REVERSE = {
    marketplace: { 0: 'browse', 1: 'listings', 2: 'received', 3: 'interests' },
    dsa: { 0: 'browse', 1: 'offers', 2: 'received', 3: 'sent' },
    collaborations: { 0: 'active', 1: 'completed' }
};

// Lazy loaded tab components
const MyListingsTab = lazy(() => import('./tabs/MyListingsTab'));
const BrowseLeadsTab = lazy(() => import('./tabs/BrowseLeadsTab'));
const ReceivedInterestsTab = lazy(() => import('./tabs/ReceivedInterestsTab'));
const MyInterestsTab = lazy(() => import('./tabs/MyInterestsTab'));
const BrowseDsaOffersTab = lazy(() => import('./tabs/BrowseDsaOffersTab'));
const MyDsaOffersTab = lazy(() => import('./tabs/MyDsaOffersTab'));
const ReceivedDsaInvitesTab = lazy(() => import('./tabs/ReceivedDsaInvitesTab'));
const SentDsaInvitesTab = lazy(() => import('./tabs/SentDsaInvitesTab'));
const ActiveCollaborationsTab = lazy(() => import('./tabs/ActiveCollaborationsTab'));
const CompletedCollaborationsTab = lazy(() => import('./tabs/CompletedCollaborationsTab'));

// Custom compact styles
const compactStyles = `
    .marketplace-dashboard {
        --compact-scale: 0.85;
        font-size: 0.875rem;
    }

    .marketplace-dashboard .p-tabview-nav {
        gap: 0.25rem;
        flex-wrap: wrap;
    }

    .marketplace-dashboard .p-tabview-nav-link {
        padding: 0.5rem 1rem;
        font-size: 0.875rem;
    }

    /* Custom main tabs container */
    .marketplace-dashboard .main-tabs-container {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        border-bottom: 2px solid var(--surface-200);
        margin-bottom: 1rem;
    }

    .marketplace-dashboard .main-tab {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        font-size: 0.9375rem;
        font-weight: 500;
        color: var(--text-color-secondary);
        background: transparent;
        border: none;
        border-bottom: 3px solid transparent;
        margin-bottom: -2px;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
    }

    .marketplace-dashboard .main-tab:hover {
        color: var(--primary-color);
        background: var(--surface-50);
    }

    .marketplace-dashboard .main-tab.active {
        color: var(--primary-color);
        border-bottom-color: var(--primary-color);
    }

    .marketplace-dashboard .main-tab i {
        font-size: 1rem;
    }

    .marketplace-dashboard .main-tab-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 1.5rem;
        height: 1.5rem;
        padding: 0 0.375rem;
        border-radius: 50px;
        font-size: 0.75rem;
        font-weight: 600;
        background: var(--surface-200);
        color: var(--text-color-secondary);
    }

    .marketplace-dashboard .main-tab.active .main-tab-count {
        background: var(--primary-color);
        color: white;
    }

    /* Hide default TabView nav */
    .marketplace-dashboard > .p-tabview > .p-tabview-nav-container {
        display: none !important;
    }

    /* Pill-style sub-tabs */
    .marketplace-dashboard .sub-tabs-container {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 1rem;
        flex-wrap: wrap;
    }

    .marketplace-dashboard .pill-tab {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        border-radius: 50px;
        font-size: 0.875rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        border: none;
        background: var(--surface-100);
        color: var(--text-color-secondary);
    }

    .marketplace-dashboard .pill-tab:hover {
        background: var(--surface-200);
    }

    .marketplace-dashboard .pill-tab.active {
        background: var(--primary-color);
        color: white;
    }

    .marketplace-dashboard .pill-tab i {
        font-size: 0.875rem;
    }

    .marketplace-dashboard .pill-tab .tab-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 1.5rem;
        height: 1.5rem;
        padding: 0 0.375rem;
        border-radius: 50px;
        font-size: 0.75rem;
        font-weight: 600;
        background: rgba(0, 0, 0, 0.1);
    }

    .marketplace-dashboard .pill-tab.active .tab-count {
        background: rgba(255, 255, 255, 0.25);
        color: white;
    }

    /* Hide default TabView nav for sub-tabs */
    .marketplace-dashboard .sub-tabs .p-tabview-nav-container {
        display: none;
    }

    .marketplace-dashboard .sub-tabs .p-tabview-panels {
        padding: 0;
    }

    .marketplace-dashboard .mobile-bottom-nav {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: var(--surface-card);
        border-top: 1px solid var(--surface-border);
        display: none;
        justify-content: space-around;
        padding: 0.5rem 0;
        z-index: 1000;
    }

    .marketplace-dashboard .mobile-bottom-nav .nav-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0.5rem 0.5rem;
        color: var(--text-color-secondary);
        font-size: 0.6875rem;
        cursor: pointer;
        transition: color 0.2s;
        flex: 1;
        text-align: center;
        white-space: nowrap;
    }

    .marketplace-dashboard .mobile-bottom-nav .nav-item.active {
        color: var(--primary-color);
    }

    .marketplace-dashboard .mobile-bottom-nav .nav-item i {
        font-size: 1.25rem;
        margin-bottom: 0.125rem;
    }

    @media (max-width: 768px) {
        .marketplace-dashboard .mobile-bottom-nav {
            display: flex;
        }

        /* Hide main tabs on mobile - bottom nav provides this navigation */
        .marketplace-dashboard .main-tabs-container {
            display: none;
        }

        .marketplace-dashboard {
            padding-bottom: 70px;
        }

        .marketplace-dashboard > .p-tabview > .p-tabview-nav {
            display: none;
        }

        .marketplace-dashboard .p-tabview-panels {
            padding: 0.5rem;
        }

        .marketplace-dashboard .sub-tabs-container {
            overflow-x: auto;
            flex-wrap: nowrap;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 0.5rem;
        }

        .marketplace-dashboard .pill-tab {
            flex-shrink: 0;
            padding: 0.375rem 0.75rem;
            font-size: 0.8125rem;
        }
    }

    .marketplace-dashboard .fab-container {
        position: fixed;
        bottom: 80px;
        right: 1rem;
        z-index: 999;
    }

    @media (min-width: 769px) {
        .marketplace-dashboard .fab-container {
            bottom: 2rem;
            right: 0.75rem;
        }
    }

    /* FAB SpeedDial styling to match prototype */
    .marketplace-dashboard .fab-container .p-speeddial-button {
        width: 56px;
        height: 56px;
        border-radius: 50%;
    }

    @media (min-width: 769px) {
        .marketplace-dashboard .fab-container .p-speeddial-button {
            width: 48px;
            height: 48px;
        }

        .marketplace-dashboard .fab-menu-icon {
            width: 36px;
            height: 36px;
        }

        .marketplace-dashboard .fab-menu-label {
            padding: 0.5rem 0.875rem;
            font-size: 0.8125rem;
        }
    }

    .marketplace-dashboard .fab-container .p-speeddial-list {
        gap: 0.75rem;
        align-items: flex-end;
    }

    /* Custom FAB menu item styling */
    .marketplace-dashboard .fab-menu-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        cursor: pointer;
    }

    .marketplace-dashboard .fab-menu-label {
        background: var(--surface-card);
        color: var(--text-color);
        padding: 0.625rem 1rem;
        border-radius: 8px;
        font-size: 0.875rem;
        font-weight: 500;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
        white-space: nowrap;
        transition: all 0.2s ease;
    }

    .marketplace-dashboard .fab-menu-item:hover .fab-menu-label {
        transform: translateX(-4px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    }

    .marketplace-dashboard .fab-menu-icon {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: var(--primary-color);
        color: white;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
        transition: all 0.2s ease;
    }

    .marketplace-dashboard .fab-menu-icon:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    }

    .marketplace-dashboard .fab-menu-icon i {
        font-size: 1rem;
    }

    /* Hide default action styling */
    .marketplace-dashboard .fab-container .p-speeddial-action {
        background: transparent;
        box-shadow: none;
        width: auto;
        height: auto;
    }

    .marketplace-dashboard .loading-skeleton {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 1rem;
        padding: 1rem 0;
    }

    .marketplace-dashboard .loading-skeleton .skeleton-card {
        background: var(--surface-card);
        border: 1px solid var(--surface-border);
        border-radius: 8px;
        padding: 1rem;
    }
`;

// Loading skeleton for tab content
const TabLoadingSkeleton = () => (
    <div className="loading-skeleton">
        {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton-card">
                <div className="flex align-items-center gap-2 mb-3">
                    <Skeleton width="32px" height="32px" borderRadius="4px" />
                    <div className="flex-1">
                        <Skeleton width="70%" height="0.875rem" className="mb-1" />
                        <Skeleton width="50%" height="0.75rem" />
                    </div>
                </div>
                <Skeleton width="50%" height="1.25rem" className="mb-2" />
                <Skeleton width="100%" height="3rem" className="mb-2" />
                <Skeleton width="60%" height="0.875rem" />
            </div>
        ))}
    </div>
);

const MarketplaceDashboard = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const toast = useRef(null);

    // Wallet balance
    const [walletBalance, setWalletBalance] = useState(0);

    useEffect(() => {
        const fetchBalance = async () => {
            try {
                const response = await walletService.getWallet();
                if (response.success) {
                    setWalletBalance(response.data.wallet?.available_balance);
                }
            } catch (error) {
                console.error('Failed to fetch wallet balance:', error);
            }
        };
        fetchBalance();
    }, []);

    // Main tab state - initialized from URL
    const [activeMainTab, setActiveMainTab] = useState(() => {
        const tab = searchParams.get('tab');
        return tab && MAIN_TAB_MAP[tab] !== undefined ? MAIN_TAB_MAP[tab] : 0;
    });

    // Sub-tab states - initialized from URL
    const [marketplaceSubTab, setMarketplaceSubTab] = useState(() => {
        const tab = searchParams.get('tab');
        const sub = searchParams.get('sub');
        if ((!tab || tab === 'marketplace') && sub && SUB_TAB_MAP.marketplace[sub] !== undefined) {
            return SUB_TAB_MAP.marketplace[sub];
        }
        return 0;
    });
    const [dsaOffersSubTab, setDsaOffersSubTab] = useState(() => {
        const tab = searchParams.get('tab');
        const sub = searchParams.get('sub');
        if (tab === 'dsa' && sub && SUB_TAB_MAP.dsa[sub] !== undefined) {
            return SUB_TAB_MAP.dsa[sub];
        }
        return 0;
    });
    const [collaborationsSubTab, setCollaborationsSubTab] = useState(() => {
        const tab = searchParams.get('tab');
        const sub = searchParams.get('sub');
        if (tab === 'collaborations' && sub && SUB_TAB_MAP.collaborations[sub] !== undefined) {
            return SUB_TAB_MAP.collaborations[sub];
        }
        return 0;
    });

    // Get current sub-tab based on active main tab
    const getCurrentSubTab = () => {
        switch (activeMainTab) {
            case 0: return marketplaceSubTab;
            case 1: return dsaOffersSubTab;
            case 2: return collaborationsSubTab;
            default: return 0;
        }
    };

    // Update URL when tabs change
    useEffect(() => {
        const mainTabName = MAIN_TAB_REVERSE[activeMainTab];
        const subTabIndex = getCurrentSubTab();
        const subTabName = SUB_TAB_REVERSE[mainTabName]?.[subTabIndex] || '';

        const newParams = new URLSearchParams();
        newParams.set('tab', mainTabName);
        if (subTabName) {
            newParams.set('sub', subTabName);
        }

        // Only update if params are different to avoid unnecessary history entries
        const currentTab = searchParams.get('tab');
        const currentSub = searchParams.get('sub');
        if (currentTab !== mainTabName || currentSub !== subTabName) {
            setSearchParams(newParams, { replace: false });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeMainTab, marketplaceSubTab, dsaOffersSubTab, collaborationsSubTab]);

    // Listen for URL changes (browser back/forward)
    useEffect(() => {
        const tab = searchParams.get('tab');
        const sub = searchParams.get('sub');

        // Update main tab from URL
        if (tab && MAIN_TAB_MAP[tab] !== undefined) {
            const newMainTab = MAIN_TAB_MAP[tab];
            const isMainTabChanging = newMainTab !== activeMainTab;

            if (isMainTabChanging) {
                setActiveMainTab(newMainTab);
            }

            // Update sub-tab based on main tab
            if (sub && SUB_TAB_MAP[tab]) {
                const newSubTab = SUB_TAB_MAP[tab][sub];
                if (newSubTab !== undefined) {
                    if (tab === 'marketplace' && newSubTab !== marketplaceSubTab) {
                        setMarketplaceSubTab(newSubTab);
                    } else if (tab === 'dsa' && newSubTab !== dsaOffersSubTab) {
                        setDsaOffersSubTab(newSubTab);
                    } else if (tab === 'collaborations' && newSubTab !== collaborationsSubTab) {
                        setCollaborationsSubTab(newSubTab);
                    }
                }
            } else if (isMainTabChanging) {
                // Reset to first sub-tab when main tab changes without sub param
                if (tab === 'marketplace') setMarketplaceSubTab(0);
                else if (tab === 'dsa') setDsaOffersSubTab(0);
                else if (tab === 'collaborations') setCollaborationsSubTab(0);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // Tab counts (will be updated by child components or API calls)
    const [tabCounts, setTabCounts] = useState({
        marketplace: { listings: 0, browse: 0, received: 0, interests: 0, total: 0 },
        dsaOffers: { browse: 0, myOffers: 0, received: 0, sent: 0, total: 0 },
        collaborations: { active: 0, completed: 0, total: 0 }
    });

    // Track which tabs have been loaded (for lazy loading)
    const [loadedTabs, setLoadedTabs] = useState({
        marketplace: { 0: true }, // First sub-tab loaded by default
        dsaOffers: {},
        collaborations: {}
    });

    // Update tab counts (can be called from child components)
    const updateTabCount = (category, tab, count) => {
        setTabCounts(prev => ({
            ...prev,
            [category]: { ...prev[category], [tab]: count }
        }));
    };

    // Main Tab Button Component (underline style)
    const MainTab = ({ icon, label, count, active, onClick }) => (
        <button
            className={`main-tab ${active ? 'active' : ''}`}
            onClick={onClick}
        >
            <i className={`pi ${icon}`}></i>
            <span>{label}</span>
            {count > 0 && (
                <span className="main-tab-count">
                    {count > 999 ? '999+' : count}
                </span>
            )}
        </button>
    );

    // Pill Tab Button Component (pill style for sub-tabs)
    const PillTab = ({ icon, label, count, active, onClick }) => (
        <button
            className={`pill-tab ${active ? 'active' : ''}`}
            onClick={onClick}
        >
            <i className={`pi ${icon}`}></i>
            <span>{label}</span>
            {count > 0 && (
                <span className="tab-count">
                    {count > 999 ? '999+' : count}
                </span>
            )}
        </button>
    );


    // Inject styles
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.textContent = compactStyles;
        document.head.appendChild(styleElement);
        return () => styleElement.remove();
    }, []);

    // Track loaded tabs for lazy loading
    useEffect(() => {
        if (activeMainTab === 0) {
            setLoadedTabs(prev => ({
                ...prev,
                marketplace: { ...prev.marketplace, [marketplaceSubTab]: true }
            }));
        } else if (activeMainTab === 1) {
            setLoadedTabs(prev => ({
                ...prev,
                dsaOffers: { ...prev.dsaOffers, [dsaOffersSubTab]: true }
            }));
        } else if (activeMainTab === 2) {
            setLoadedTabs(prev => ({
                ...prev,
                collaborations: { ...prev.collaborations, [collaborationsSubTab]: true }
            }));
        }
    }, [activeMainTab, marketplaceSubTab, dsaOffersSubTab, collaborationsSubTab]);

    // Error handler for tab components
    const handleTabError = (message) => {
        toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: message,
            life: 3000
        });
    };

    // FAB Actions - Only 2 options: Post Lead and Create DSA Offer
    const fabItems = [
        {
            label: 'Post a Lead',
            icon: 'pi pi-plus',
            command: () => navigate('/app/marketplace/post'),
            template: (item, options) => (
                <div className="fab-menu-item" onClick={item.command}>
                    <span className="fab-menu-label">{item.label}</span>
                    <button className={`fab-menu-icon ${options.className}`}>
                        <i className={item.icon}></i>
                    </button>
                </div>
            )
        },
        {
            label: 'Post Payout',
            icon: 'pi pi-plus',
            command: () => navigate('/app/dsa-offers/post'),
            template: (item, options) => (
                <div className="fab-menu-item" onClick={item.command}>
                    <span className="fab-menu-label">{item.label}</span>
                    <button className={`fab-menu-icon ${options.className}`}>
                        <i className={item.icon}></i>
                    </button>
                </div>
            )
        }
    ];

    // Render marketplace sub-tab content
    const renderMarketplaceContent = () => {
        switch (marketplaceSubTab) {
            case 0:
                return loadedTabs.marketplace[0] ? (
                    <Suspense fallback={<TabLoadingSkeleton />}>
                        <BrowseLeadsTab onError={handleTabError} />
                    </Suspense>
                ) : null;
            case 1:
                return loadedTabs.marketplace[1] ? (
                    <Suspense fallback={<TabLoadingSkeleton />}>
                        <MyListingsTab onError={handleTabError} />
                    </Suspense>
                ) : null;
            case 2:
                return loadedTabs.marketplace[2] ? (
                    <Suspense fallback={<TabLoadingSkeleton />}>
                        <ReceivedInterestsTab onError={handleTabError} />
                    </Suspense>
                ) : null;
            case 3:
                return loadedTabs.marketplace[3] ? (
                    <Suspense fallback={<TabLoadingSkeleton />}>
                        <MyInterestsTab onError={handleTabError} />
                    </Suspense>
                ) : null;
            default:
                return null;
        }
    };

    // Render DSA offers sub-tab content
    const renderDsaOffersContent = () => {
        switch (dsaOffersSubTab) {
            case 0:
                return loadedTabs.dsaOffers[0] ? (
                    <Suspense fallback={<TabLoadingSkeleton />}>
                        <BrowseDsaOffersTab onError={handleTabError} />
                    </Suspense>
                ) : null;
            case 1:
                return loadedTabs.dsaOffers[1] ? (
                    <Suspense fallback={<TabLoadingSkeleton />}>
                        <MyDsaOffersTab onError={handleTabError} />
                    </Suspense>
                ) : null;
            case 2:
                return loadedTabs.dsaOffers[2] ? (
                    <Suspense fallback={<TabLoadingSkeleton />}>
                        <ReceivedDsaInvitesTab onError={handleTabError} />
                    </Suspense>
                ) : null;
            case 3:
                return loadedTabs.dsaOffers[3] ? (
                    <Suspense fallback={<TabLoadingSkeleton />}>
                        <SentDsaInvitesTab onError={handleTabError} />
                    </Suspense>
                ) : null;
            default:
                return null;
        }
    };

    // Render collaborations sub-tab content
    const renderCollaborationsContent = () => {
        switch (collaborationsSubTab) {
            case 0:
                return loadedTabs.collaborations[0] ? (
                    <Suspense fallback={<TabLoadingSkeleton />}>
                        <ActiveCollaborationsTab onError={handleTabError} />
                    </Suspense>
                ) : null;
            case 1:
                return loadedTabs.collaborations[1] ? (
                    <Suspense fallback={<TabLoadingSkeleton />}>
                        <CompletedCollaborationsTab onError={handleTabError} />
                    </Suspense>
                ) : null;
            default:
                return null;
        }
    };

    return (
        <div className="marketplace-dashboard">
            <Toast ref={toast} />

            <div className="flex align-items-center justify-content-between mb-3">
                <h2 className="text-900 font-bold m-0" style={{ fontSize: '1rem' }}>Dashboard</h2>
                <div className="flex align-items-center gap-2 cursor-pointer" onClick={() => navigate('/app/leadflow/wallet')} title="Commission Wallet">
                    <i className="pi pi-wallet text-primary" style={{ fontSize: '0.9rem' }}></i>
                    <span className="text-500 hidden md:inline" style={{ fontSize: '0.8rem' }}>Commission Wallet:</span>
                    <span className="text-900 font-semibold" style={{ fontSize: '0.875rem' }}>{formatCurrency(walletBalance)}</span>
                </div>
            </div>

            {/* Custom Main Tabs */}
            <div className="main-tabs-container">
                <MainTab
                    icon="pi-shopping-bag"
                    label="Buy and Sell Leads"
                    count={tabCounts.marketplace.total}
                    active={activeMainTab === 0}
                    onClick={() => {
                        if (activeMainTab !== 0) {
                            setMarketplaceSubTab(0);
                        }
                        setActiveMainTab(0);
                    }}
                />
                <MainTab
                    icon="pi-percentage"
                    label="Commission Payouts"
                    count={tabCounts.dsaOffers.total}
                    active={activeMainTab === 1}
                    onClick={() => {
                        if (activeMainTab !== 1) {
                            setDsaOffersSubTab(0);
                        }
                        setActiveMainTab(1);
                    }}
                />
                <MainTab
                    icon="pi-briefcase"
                    label="Lead Deals"
                    count={tabCounts.collaborations.total}
                    active={activeMainTab === 2}
                    onClick={() => {
                        if (activeMainTab !== 2) {
                            setCollaborationsSubTab(0);
                        }
                        setActiveMainTab(2);
                    }}
                />
            </div>

            {/* Main TabView (hidden nav, content only) */}
            <TabView
                activeIndex={activeMainTab}
                onTabChange={(e) => {
                    const newTab = e.index;
                    if (newTab !== activeMainTab) {
                        // Reset sub-tab to first when switching main tabs
                        if (newTab === 0) setMarketplaceSubTab(0);
                        else if (newTab === 1) setDsaOffersSubTab(0);
                        else if (newTab === 2) setCollaborationsSubTab(0);
                    }
                    setActiveMainTab(newTab);
                }}
                className="mb-0"
            >
                {/* Buy and Sell Leads Tab */}
                <TabPanel>
                    {/* Pill-style Sub-tabs */}
                    <div className="sub-tabs-container">
                        <PillTab
                            icon="pi-search"
                            label="Discover Leads"
                            count={tabCounts.marketplace.browse}
                            active={marketplaceSubTab === 0}
                            onClick={() => setMarketplaceSubTab(0)}
                        />
                        <PillTab
                            icon="pi-list"
                            label="My Listings"
                            count={tabCounts.marketplace.listings}
                            active={marketplaceSubTab === 1}
                            onClick={() => setMarketplaceSubTab(1)}
                        />
                        <PillTab
                            icon="pi-inbox"
                            label="Buyer Enquiries"
                            count={tabCounts.marketplace.received}
                            active={marketplaceSubTab === 2}
                            onClick={() => setMarketplaceSubTab(2)}
                        />
                        <PillTab
                            icon="pi-bookmark"
                            label="Enquired Leads"
                            count={tabCounts.marketplace.interests}
                            active={marketplaceSubTab === 3}
                            onClick={() => setMarketplaceSubTab(3)}
                        />
                    </div>
                    {renderMarketplaceContent()}
                </TabPanel>

                {/* Commission Payouts Tab */}
                <TabPanel>
                    {/* Pill-style Sub-tabs */}
                    <div className="sub-tabs-container">
                        <PillTab
                            icon="pi-search"
                            label="Discover Payout"
                            count={tabCounts.dsaOffers.browse}
                            active={dsaOffersSubTab === 0}
                            onClick={() => setDsaOffersSubTab(0)}
                        />
                        <PillTab
                            icon="pi-list"
                            label="My Payouts"
                            count={tabCounts.dsaOffers.myOffers}
                            active={dsaOffersSubTab === 1}
                            onClick={() => setDsaOffersSubTab(1)}
                        />
                        <PillTab
                            icon="pi-inbox"
                            label="Payout Enquiries"
                            count={tabCounts.dsaOffers.received}
                            active={dsaOffersSubTab === 2}
                            onClick={() => setDsaOffersSubTab(2)}
                        />
                        <PillTab
                            icon="pi-send"
                            label="My Enquiries"
                            count={tabCounts.dsaOffers.sent}
                            active={dsaOffersSubTab === 3}
                            onClick={() => setDsaOffersSubTab(3)}
                        />
                    </div>
                    {renderDsaOffersContent()}
                </TabPanel>

                {/* Lead Deals Tab */}
                <TabPanel>
                    {/* Pill-style Sub-tabs */}
                    <div className="sub-tabs-container">
                        <PillTab
                            icon="pi-briefcase"
                            label="My Deals"
                            count={tabCounts.collaborations.active}
                            active={collaborationsSubTab === 0}
                            onClick={() => setCollaborationsSubTab(0)}
                        />
                        <PillTab
                            icon="pi-check-circle"
                            label="Lead Conversions"
                            count={tabCounts.collaborations.completed}
                            active={collaborationsSubTab === 1}
                            onClick={() => setCollaborationsSubTab(1)}
                        />
                    </div>
                    {renderCollaborationsContent()}
                </TabPanel>
            </TabView>

            {/* FAB Button */}
            <div className="fab-container">
                <SpeedDial
                    model={fabItems}
                    direction="up"
                    showIcon="pi pi-plus"
                    hideIcon="pi pi-times"
                    buttonClassName="p-button-primary"
                    style={{ position: 'relative' }}
                />
            </div>

            {/* Mobile Bottom Navigation - 3 tabs matching top navigation */}
            <div className="mobile-bottom-nav">
                <div
                    className={`nav-item ${activeMainTab === 0 ? 'active' : ''}`}
                    onClick={() => {
                        if (activeMainTab !== 0) setMarketplaceSubTab(0);
                        setActiveMainTab(0);
                    }}
                >
                    <i className="pi pi-shopping-bag"></i>
                    <span>Buy and Sell</span>
                </div>
                <div
                    className={`nav-item ${activeMainTab === 1 ? 'active' : ''}`}
                    onClick={() => {
                        if (activeMainTab !== 1) setDsaOffersSubTab(0);
                        setActiveMainTab(1);
                    }}
                >
                    <i className="pi pi-percentage"></i>
                    <span>Commission Payout</span>
                </div>
                <div
                    className={`nav-item ${activeMainTab === 2 ? 'active' : ''}`}
                    onClick={() => {
                        if (activeMainTab !== 2) setCollaborationsSubTab(0);
                        setActiveMainTab(2);
                    }}
                >
                    <i className="pi pi-briefcase"></i>
                    <span>Deals</span>
                </div>
            </div>
        </div>
    );
};

export default MarketplaceDashboard;
